import { getErrMsg } from '../lib/errorMsg';
import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { UserPlus, Shield, Trash2, RefreshCw, Key, Eye, EyeOff, Check, X as XIcon, Edit2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';

const ROLE_STYLES = {
  admin: 'bg-red-50 text-red-700 border-red-200',
  accounts_head: 'bg-purple-50 text-purple-700 border-purple-200',
  costing_editor: 'bg-blue-50 text-blue-700 border-blue-200',
  viewer: 'bg-slate-100 text-slate-600 border-slate-200',
};
const ROLE_LABELS = {
  admin: 'Admin',
  accounts_head: 'Accounts Head',
  costing_editor: 'Costing Editor',
  viewer: 'Viewer',
};

function AddUserModal({ onClose, onAdded }) {
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'viewer' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.email || !form.name || !form.password) {
      toast.error('Please fill all required fields');
      return;
    }
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/users/invite', {
        email: form.email,
        name: form.name,
        initial_password: form.password,
        role: form.role,
      });
      toast.success(`User ${form.name} invited successfully`);
      onAdded();
      onClose();
    } catch (err) {
      toast.error(getErrMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-slate-900">Add New User</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {[
            { label: 'Full Name *', key: 'name', type: 'text', placeholder: 'Rahul Sharma' },
            { label: 'Email *', key: 'email', type: 'email', placeholder: 'rahul@anmolmetcomp.com' },
            { label: 'Password *', key: 'password', type: 'password', placeholder: 'Min. 8 characters' },
          ].map(({ label, key, type, placeholder }) => (
            <div key={key}>
              <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">{label}</label>
              <input type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder}
                data-testid={`add-user-${key}`}
                className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
            </div>
          ))}
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">Role *</label>
            <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
              data-testid="add-user-role"
              className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900">
              <option value="viewer">Viewer — Read-only access</option>
              <option value="costing_editor">Costing Editor — Can edit RM & scrap prices</option>
              <option value="accounts_head">Accounts Head — Cross-section visibility, bulk revise</option>
              <option value="admin">Admin — Full platform access</option>
            </select>
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 h-9 border border-slate-300 rounded-sm text-slate-700 text-sm font-semibold hover:bg-slate-50">Cancel</button>
            <button onClick={handleSave} disabled={saving} data-testid="create-user-btn"
              className="flex-1 h-9 bg-slate-900 text-white text-sm font-bold uppercase rounded-sm hover:bg-slate-800 disabled:opacity-50 transition-colors">
              {saving ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ChangeOwnPasswordModal({ onClose }) {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const handleSave = async () => {
    if (!form.current_password || !form.new_password) {
      toast.error('Please fill all fields'); return;
    }
    if (form.new_password.length < 6) {
      toast.error('New password must be at least 6 characters'); return;
    }
    if (form.new_password !== form.confirm) {
      toast.error('Passwords do not match'); return;
    }
    setSaving(true);
    try {
      await api.put('/auth/change-password', { current_password: form.current_password, new_password: form.new_password });
      toast.success('Password changed successfully');
      onClose();
    } catch (err) {
      toast.error(getErrMsg(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-heading text-slate-900">Change Your Password</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">Current Password</label>
            <div className="relative">
              <input type={showCurrent ? 'text' : 'password'} value={form.current_password}
                onChange={e => setForm(f => ({ ...f, current_password: e.target.value }))}
                data-testid="current-password-input" autoComplete="new-password"
                className="w-full border border-slate-300 rounded-sm h-9 px-3 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
              <button type="button" onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 z-10 p-0.5">
                {showCurrent ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">New Password</label>
            <div className="relative">
              <input type={showNew ? 'text' : 'password'} value={form.new_password}
                onChange={e => setForm(f => ({ ...f, new_password: e.target.value }))}
                data-testid="new-password-input" placeholder="Min. 6 characters" autoComplete="new-password"
                className="w-full border border-slate-300 rounded-sm h-9 px-3 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
              <button type="button" onClick={() => setShowNew(!showNew)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 z-10 p-0.5">
                {showNew ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">Confirm New Password</label>
            <input type="password" value={form.confirm}
              onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
              data-testid="confirm-password-input" autoComplete="new-password"
              className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 h-9 border border-slate-300 rounded-sm text-slate-700 text-sm font-semibold hover:bg-slate-50">Cancel</button>
            <button onClick={handleSave} disabled={saving} data-testid="save-password-btn"
              className="flex-1 h-9 bg-slate-900 text-white text-sm font-bold uppercase rounded-sm hover:bg-slate-800 disabled:opacity-50">
              {saving ? 'Saving...' : 'Change Password'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordModal({ user, onClose }) {
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const handleSave = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters'); return;
    }
    setSaving(true);
    try {
      await api.put(`/users/${user.user_id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${user.name}`);
      onClose();
    } catch (err) {
      toast.error(getErrMsg(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-heading text-slate-900">Reset Password — {user.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <p className="text-sm text-slate-500">Set a new password for <strong>{user.email}</strong></p>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">New Password</label>
            <div className="relative">
              <input type={showPw ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)}
                data-testid="reset-password-input" placeholder="Min. 6 characters" autoComplete="new-password"
                className="w-full border border-slate-300 rounded-sm h-9 px-3 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
              <button type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 z-10 p-0.5">
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 h-9 border border-slate-300 rounded-sm text-slate-700 text-sm font-semibold hover:bg-slate-50">Cancel</button>
            <button onClick={handleSave} disabled={saving} data-testid="confirm-reset-btn"
              className="flex-1 h-9 bg-red-600 text-white text-sm font-bold uppercase rounded-sm hover:bg-red-700 disabled:opacity-50">
              {saving ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [updatingRole, setUpdatingRole] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState(new Set());
  const [editingPassword, setEditingPassword] = useState(null); // user_id being edited
  const [editPwValue, setEditPwValue] = useState('');
  const [savingPw, setSavingPw] = useState(null);

  const togglePasswordVisibility = (userId) => {
    setVisiblePasswords(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const startEditPassword = (userId, currentDisplay) => {
    setEditingPassword(userId);
    setEditPwValue(currentDisplay || '');
  };

  const cancelEditPassword = () => {
    setEditingPassword(null);
    setEditPwValue('');
  };

  const saveInlinePassword = async (userId) => {
    if (!editPwValue || editPwValue.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSavingPw(userId);
    try {
      await api.put(`/users/${userId}/reset-password`, { new_password: editPwValue });
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, password_display: editPwValue } : u));
      toast.success('Password updated');
      setEditingPassword(null);
      setEditPwValue('');
    } catch (err) {
      toast.error(getErrMsg(err));
    } finally {
      setSavingPw(null);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/users');
      setUsers(res.data);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleRoleChange = async (userId, newRole) => {
    setUpdatingRole(userId);
    try {
      await api.put(`/users/${userId}/role`, { role: newRole });
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, role: newRole } : u));
      toast.success('Role updated successfully');
    } catch (err) {
      toast.error(getErrMsg(err));
    } finally {
      setUpdatingRole(null);
    }
  };

  const handleDelete = async (user) => {
    if (user.user_id === currentUser?.user_id) {
      toast.error('You cannot delete your own account');
      return;
    }
    if (!window.confirm(`Delete user ${user.name} (${user.email})? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${user.user_id}`);
      setUsers(prev => prev.filter(u => u.user_id !== user.user_id));
      toast.success('User deleted');
    } catch {
      toast.error('Failed to delete user');
    }
  };

  return (
    <div className="space-y-5 max-w-4xl" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-slate-900 text-xl">User Management</h2>
          <p className="text-slate-500 text-sm mt-0.5">{users.length} users on the platform</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowChangePassword(true)} data-testid="change-password-btn"
            className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 rounded-sm text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">
            <Key size={13} /> Change My Password
          </button>
          <button onClick={fetchUsers} data-testid="refresh-users"
            className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 rounded-sm text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">
            <RefreshCw size={13} /> Refresh
          </button>
          <button onClick={() => setShowAdd(true)} data-testid="add-user-btn"
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-slate-800 transition-colors shadow-sm">
            <UserPlus size={15} /> Add User
          </button>
        </div>
      </div>

      {/* Role Guide */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <Shield size={14} className="text-slate-500" />
          <p className="text-xs font-bold uppercase text-slate-500">Role Permissions</p>
        </div>
        <div className="grid grid-cols-3 gap-4 text-xs">
          {[
            { role: 'admin', label: 'Admin', perms: 'Full access — manage users, all data, imports, exports' },
            { role: 'accounts_head', label: 'Accounts Head', perms: 'Cross-section visibility, bulk revise, single-customer export' },
            { role: 'costing_editor', label: 'Costing Editor', perms: 'Edit RM & scrap prices only' },
            { role: 'viewer', label: 'Viewer', perms: 'Read-only — watermarked views' },
          ].map(({ role, label, perms }) => (
            <div key={role} className="flex items-start gap-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border mt-0.5 flex-shrink-0 ${ROLE_STYLES[role]}`}>
                {label}
              </span>
              <p className="text-slate-500 leading-relaxed">{perms}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto mb-3"></div>
            <p className="text-slate-400 text-sm">Loading users...</p>
          </div>
        ) : (
          <table className="data-table w-full">
            <thead>
              <tr>
                <th className="text-left">User</th>
                <th className="text-left">Email</th>
                <th className="text-left">Password</th>
                <th className="text-left">Role</th>
                <th className="text-left">Joined</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const isVisible = visiblePasswords.has(u.user_id);
                const isEditing = editingPassword === u.user_id;
                return (
                <tr key={u.user_id} className="group">
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                        {u.picture ? (
                          <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <span className="text-slate-600 text-sm font-bold">{u.name?.[0]?.toUpperCase()}</span>
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">{u.name}</p>
                        {u.user_id === currentUser?.user_id && (
                          <p className="text-xs text-orange-500 font-medium">You</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="font-mono text-xs text-slate-600">{u.email}</td>
                  <td>
                    {isEditing ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={editPwValue}
                          onChange={e => setEditPwValue(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveInlinePassword(u.user_id); if (e.key === 'Escape') cancelEditPassword(); }}
                          data-testid={`edit-pw-input-${u.user_id}`}
                          autoFocus
                          className="border border-slate-300 rounded-sm h-7 px-2 text-xs font-mono w-32 focus:outline-none focus:ring-1 focus:ring-slate-900"
                        />
                        <button onClick={() => saveInlinePassword(u.user_id)} disabled={savingPw === u.user_id}
                          data-testid={`save-pw-${u.user_id}`}
                          className="p-1 text-emerald-600 hover:bg-emerald-50 rounded-sm transition-colors">
                          <Check size={14} />
                        </button>
                        <button onClick={cancelEditPassword}
                          className="p-1 text-slate-400 hover:bg-slate-100 rounded-sm transition-colors">
                          <XIcon size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-xs text-slate-600 min-w-[80px]" data-testid={`pw-display-${u.user_id}`}>
                          {u.password_display
                            ? (isVisible ? u.password_display : '••••••••')
                            : <span className="text-slate-400 italic">not set</span>
                          }
                        </span>
                        {u.password_display && (
                          <button onClick={() => togglePasswordVisibility(u.user_id)}
                            data-testid={`pw-toggle-${u.user_id}`}
                            className="p-0.5 text-slate-400 hover:text-slate-600 transition-colors">
                            {isVisible ? <EyeOff size={12} /> : <Eye size={12} />}
                          </button>
                        )}
                        <button onClick={() => startEditPassword(u.user_id, u.password_display || '')}
                          data-testid={`pw-edit-${u.user_id}`}
                          className="p-0.5 text-slate-400 hover:text-slate-600 transition-colors opacity-0 group-hover:opacity-100">
                          <Edit2 size={12} />
                        </button>
                      </div>
                    )}
                  </td>
                  <td>
                    <select
                      value={u.role}
                      onChange={e => handleRoleChange(u.user_id, e.target.value)}
                      disabled={updatingRole === u.user_id || u.user_id === currentUser?.user_id}
                      data-testid={`role-select-${u.user_id}`}
                      className={`border border-slate-300 rounded-sm h-7 px-2 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-slate-900 disabled:opacity-60 disabled:cursor-not-allowed ${ROLE_STYLES[u.role]}`}
                    >
                      <option value="viewer">Viewer</option>
                      <option value="costing_editor">Costing Editor</option>
                      <option value="accounts_head">Accounts Head</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td className="font-mono text-xs text-slate-500">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td className="text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      {u.user_id !== currentUser?.user_id && (
                        <button onClick={() => handleDelete(u)}
                          data-testid={`delete-user-${u.user_id}`}
                          className="p-1.5 text-red-400 hover:bg-red-50 rounded-sm transition-colors opacity-0 group-hover:opacity-100">
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && <AddUserModal onClose={() => setShowAdd(false)} onAdded={fetchUsers} />}
      {showChangePassword && <ChangeOwnPasswordModal onClose={() => setShowChangePassword(false)} />}
      {resetPasswordUser && <ResetPasswordModal user={resetPasswordUser} onClose={() => setResetPasswordUser(null)} />}
    </div>
  );
}
